// Lightweight client monitoring: global error listeners and web-vitals reporting.
// No external vendors; posts to local endpoints which are safe in dev and optional in prod.

type LogEvent = {
  type: string;
  message?: string;
  stack?: string;
  meta?: Record<string, any>;
};

// Simple backoff if API is unavailable (e.g., static-only deployments)
let logsDisabledUntil = 0;
const backoffMs = 10 * 60 * 1000; // 10 minutes

function getMetricsBearer(): string {
  try {
    const local = (typeof window !== 'undefined' && window.localStorage && (
      localStorage.getItem('METRICS_BEARER') ||
      localStorage.getItem('metrics_bearer') ||
      localStorage.getItem('dev_metrics_bearer')
    )) || '';
    if (local && local.trim()) return local.trim();
    try {
      const envTok = (import.meta as any)?.env?.VITE_METRICS_BEARER as string | undefined;
      if (envTok && String(envTok).trim()) return String(envTok).trim();
    } catch {}
    const globalAny: any = typeof window !== 'undefined' ? (window as any) : {};
    const injected = String(globalAny.__METRICS_BEARER__ || '').trim();
    if (injected) return injected;
  } catch {}
  return '';
}

function post(path: string, payload: any) {
  try {
    // Skip posting if we've recently seen a 404 or network failure
    if (Date.now() < logsDisabledUntil) return;
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const bearer = getMetricsBearer();
    // In production, only post when a bearer is present to avoid 404 noise
    try {
      const env: any = (import.meta as any)?.env || {};
      const isDev = env?.DEV === true;
      const allowFlag = String(env?.VITE_ALLOW_CLIENT_LOGS || '').trim() === '1' || String(env?.VITE_ENABLE_CLIENT_LOGS || '').trim() === '1';
      // Production gating: require either a bearer token OR explicit allow flag
      if (!isDev && !bearer && !allowFlag) return;
    } catch {}
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
    fetch(path, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })
      .then((res) => {
        // If endpoint does not exist, back off to avoid console noise
        if (!res.ok && (res.status === 404 || res.status === 405)) {
          logsDisabledUntil = Date.now() + backoffMs;
        }
      })
      .catch(() => {
        // Network error: also back off briefly
        logsDisabledUntil = Date.now() + backoffMs;
      });
  } catch {}
}

export function initMonitoring() {
  // Guard against double-init
  if ((window as any).__monitoring_init) return;
  (window as any).__monitoring_init = true;

  // Global runtime errors
  window.addEventListener('error', (e) => {
    const ev: LogEvent = {
      type: 'window-error',
      message: String(e?.error?.message || e?.message || 'Unknown error'),
      stack: String((e?.error && (e.error as any).stack) || ''),
      meta: {
        filename: (e as any).filename,
        lineno: (e as any).lineno,
        colno: (e as any).colno,
      },
    };
    post('/api/client-logs', ev);
  });

  // Unhandled Promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    const reason: any = (e as any).reason;
    const ev: LogEvent = {
      type: 'unhandled-rejection',
      message: String(reason?.message || reason || 'unhandledrejection'),
      stack: String(reason?.stack || ''),
    };
    post('/api/client-logs', ev);
  });

  // Web-vitals (optional): If a global web-vitals script is present, hook it; else no-op.
  setTimeout(() => {
    try {
      const mod: any = (window as any).webVitals;
      if (!mod) return;
      const send = (metric: any) => {
        post('/api/telemetry', {
          type: 'web-vitals',
          name: metric.name,
          id: metric.id,
          value: metric.value,
          rating: metric.rating,
          navigationType: (performance as any).navigation?.type,
          ts: Date.now(),
        });
      };
      try { mod.onCLS(send); } catch {}
      try { mod.onFID(send); } catch {}
      try { mod.onLCP(send); } catch {}
      try { mod.onINP?.(send); } catch {}
      try { mod.onTTFB(send); } catch {}
    } catch {}
  }, 0);
}
